import React, { useState, useEffect } from 'react'
import { Fingerprint, Plus, Trash2, Loader, Smartphone, Laptop, Key } from 'lucide-react'
import { startRegistration } from '@simplewebauthn/browser'
import type { PasskeyCredential } from '../lib/storage'

const PasskeySettings: React.FC = () => {
  const [passkeys, setPasskeys] = useState<PasskeyCredential[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [deviceName, setDeviceName] = useState('')
  const [passkeySupported, setPasskeySupported] = useState(false)

  useEffect(() => {
    // Check if WebAuthn is supported
    if (window.PublicKeyCredential) {
      setPasskeySupported(true)
    }

    loadPasskeys()
  }, [])

  const loadPasskeys = async () => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/auth/user/passkeys', {
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        setPasskeys(data.passkeys || [])
      }
    } catch (err) {
      console.error('Failed to load passkeys:', err)
    }
  }

  const handleAddPasskey = async () => {
    if (!deviceName.trim()) {
      setError('Please enter a device name')
      return
    }

    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      const token = localStorage.getItem('auth-token')

      // Get registration options from server
      const optionsResponse = await fetch('/api/auth/passkey/register/options', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!optionsResponse.ok) {
        const data = await optionsResponse.json()
        throw new Error(data.error || 'Failed to get registration options')
      }

      const options = await optionsResponse.json()

      // Trigger device authentication (TouchID, FaceID, etc.)
      const credential = await startRegistration(options)

      // Verify registration with server
      const verifyResponse = await fetch('/api/auth/passkey/register/verify', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          credential,
          deviceName: deviceName.trim()
        })
      })

      if (!verifyResponse.ok) {
        const data = await verifyResponse.json()
        throw new Error(data.error || 'Registration failed')
      }

      const data = await verifyResponse.json()

      if (data.success) {
        setSuccess('Passkey added successfully!')
        setShowAddModal(false)
        setDeviceName('')
        await loadPasskeys()
      } else {
        throw new Error('Registration failed')
      }

    } catch (err: any) {
      console.error('Passkey registration error:', err, {
        name: err?.name,
        message: err?.message,
        stack: err?.stack
      })
      setError(err.message || 'Failed to add passkey')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeletePasskey = async (credentialId: string) => {
    if (!confirm('Are you sure you want to remove this passkey?')) {
      return
    }

    try {
      const token = localStorage.getItem('auth-token')
      const response = await fetch('/api/auth/passkey/delete', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ credentialId })
      })

      if (response.ok) {
        setSuccess('Passkey removed successfully')
        await loadPasskeys()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to remove passkey')
      }
    } catch (err) {
      console.error('Failed to delete passkey:', err)
      setError('Failed to remove passkey')
    }
  }

  const getDeviceIcon = (deviceName?: string) => {
    const name = deviceName?.toLowerCase() || ''
    if (name.includes('iphone') || name.includes('android') || name.includes('phone')) {
      return <Smartphone className="h-5 w-5" />
    } else if (name.includes('mac') || name.includes('laptop') || name.includes('computer')) {
      return <Laptop className="h-5 w-5" />
    }
    return <Key className="h-5 w-5" />
  }

  if (!passkeySupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          Your browser does not support passkeys. Please use a modern browser like Chrome, Safari, or Edge.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Passkeys</h3>
          <p className="text-sm text-gray-600 mt-1">
            Use TouchID, FaceID, or Windows Hello to sign in securely without passwords
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Passkey
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {passkeys.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Fingerprint className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No passkeys yet</h4>
          <p className="text-sm text-gray-600 mb-4">
            Add a passkey to enable fast, secure sign-in with TouchID, FaceID, or Windows Hello
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {passkeys.map((passkey) => (
            <div
              key={passkey.credentialId}
              className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div className="text-gray-600">
                  {getDeviceIcon(passkey.deviceName)}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{passkey.deviceName || 'Unknown Device'}</p>
                  <p className="text-sm text-gray-500">
                    Added {new Date(passkey.createdAt).toLocaleDateString()}
                    {passkey.lastUsedAt && (
                      <span className="ml-2">
                        • Last used {new Date(passkey.lastUsedAt).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDeletePasskey(passkey.credentialId)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                title="Remove passkey"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Passkey Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Add New Passkey</h3>
            <p className="text-sm text-gray-600">
              Give this passkey a name to help you remember which device it's for
            </p>

            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="e.g., MacBook Pro, iPhone 15"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
              autoFocus
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setDeviceName('')
                  setError('')
                }}
                disabled={isLoading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPasskey}
                disabled={isLoading || !deviceName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Fingerprint className="h-4 w-4" />
                    Add Passkey
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              You'll be prompted to use TouchID, FaceID, or Windows Hello to create your passkey
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default PasskeySettings
